import React from "react";
import { CodeBlock } from "@ui/CodeBlock";

function DeveloperNodeSDK() {
    return (
        <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:py-12 lg:px-8">
            <h1 className="text-3xl font-bold text-gray-900  mb-8">
                Agent UI SDK Documentation
            </h1>

            <p className="text-gray-600  mb-8">
                A simple and powerful SDK for integrating with the Agent UI API.
                This SDK allows you to easily register and manage functions for
                your agent-based applications.
            </p>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Installation
                </h2>
                <CodeBlock language="bash">
                    {`npm install agent-ui-sdk
# or
yarn add agent-ui-sdk`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Quick Start
                </h2>
                <CodeBlock language="javascript">
                    {`import { SDKClient, FunctionType } from 'agent-ui-sdk';

// Initialize the client
const client = new SDKClient({
  apiKey: 'your-api-key',
  baseUrl: 'https://api.agentui.ai' // Optional
});

// Register a function
async function registerMyFunction() {
  try {
    const result = await client.registerFunction({
      path: '/api/my-function',
      type: FunctionType.OUTGOING,
      method: 'POST',
      parameters: {
        userId: {
          type: 'string',
          description: 'The unique identifier of the user to notify'
        },
        action: {
          type: 'string',
          description: 'The action to perform on the user'
        }
      },
      headers: {
        "Authorization": "Bearer your-api-key"
      },
      contentType: 'JSON',
      requiresAuth: true,
      timeout: 5000,
      aiInstructions: 'This function is used to perform an action on a user.'
    });
    
    console.log('Function registered:', result);
  } catch (error) {
    console.error('Error:', error);
  }
}`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    API Reference
                </h2>

                <h3 className="text-xl font-semibold text-gray-900  mt-6 mb-3">
                    SDKClient
                </h3>
                <h4 className="text-lg font-medium text-gray-900  mt-4 mb-2">
                    Constructor Options
                </h4>
                <CodeBlock language="typescript">
                    {`interface ClientOptions {
  apiKey: string;          // Required: Your API key
  baseUrl?: string;        // Optional: API base URL
  timeout?: number;        // Optional: Request timeout in ms
}`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Function Types
                </h2>
                <p className="text-gray-600  mb-4">
                    The SDK supports three types of functions that can be
                    registered:
                </p>

                <div className="space-y-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900  mb-2">
                            OUTGOING
                        </h3>
                        <p className="text-gray-600  mb-2">
                            Functions that your agent calls to perform actions
                            or retrieve data. These are typically API endpoints
                            that your agent can use.
                        </p>
                        <CodeBlock language="javascript">
                            {`const outgoingFunction = await client.registerFunction({
  path: '/api/send-notification',
  type: FunctionType.OUTGOING,
  method: 'POST',
  parameters: {
    userId: {
      type: 'string',
      description: 'The unique identifier of the user to notify'
    },
    message: {
      type: 'string',
      description: 'The notification message to be sent'
    }
  },
  headers: {
    "Authorization": "Bearer your-api-key"
  }
});`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900  mb-2">
                            INCOMING
                        </h3>
                        <p className="text-gray-600  mb-2">
                            Webhook-style functions that send data to your
                            agent. These allow external systems to trigger
                            actions or provide information to your agent.
                        </p>
                        <CodeBlock language="javascript">
                            {`const incomingFunction = await client.registerFunction({
  path: '/webhook/receive',
  type: FunctionType.INCOMING,
  method: 'POST',
  parameters: {
    eventType: {
      type: 'string',
      description: 'The type of event being received (e.g., "user.created", "payment.success")'
    },
    payload: {
      type: 'object',
      description: 'The data associated with the event'
    }
  },
  headers: {
    "Authorization": "Bearer your-api-key"
  }
});`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900  mb-2">
                            AUTHENTICATION
                        </h3>
                        <p className="text-gray-600  mb-2">
                            Special functions used to validate authentication
                            tokens or credentials. These ensure secure access to
                            your agent's functionality.
                        </p>
                        <CodeBlock language="javascript">
                            {`const authFunction = await client.registerFunction({
  path: '/auth/validate',
  type: FunctionType.AUTHENTICATION,
  method: 'POST',
  parameters: {
    token: {
      type: 'string',
      description: 'The authentication token to validate'
    }
  },
  headers: {
    "Authorization": "Bearer your-api-key"
  },
  timeout: 3000
});`}
                        </CodeBlock>
                    </div>
                </div>
            </section>

            {/* Add more sections as needed */}

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Support
                </h2>
                <p className="text-gray-600 ">
                    For support, please contact support@agentui.ai or open an
                    issue on our GitHub repository.
                </p>
            </section>
        </div>
    );
}

export default DeveloperNodeSDK;
